import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Check, Clock3, Loader2, Lock } from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import {
  clinicInfoPatchSchema,
  normalizeClinicMainPhone,
  type ClinicInfo,
} from './settings-contracts'
import { useClinicProfile } from './settings-queries'
import {
  isSettingsPermissionError,
  type SettingsRepository,
} from './settings-repository'
import { SettingsConfirmDialog } from './SettingsConfirmDialog'
import { useReportUnsavedChanges } from './settings-unsaved-changes'
import panel from './SettingsSection.module.css'
import styles from './ClinicProfileSettings.module.css'

const EMPTY_CLINIC_INFO: ClinicInfo = {
  dentistryName: '',
  dentistryAddress: '',
  dentistryPhone: '',
  dentistryEmail: '',
  emergencyPhone: '',
}

type ClinicField = keyof ClinicInfo

type FieldConfig = {
  field: ClinicField
  label: string
  type?: 'email' | 'tel'
  autoComplete: string
  hint?: string
  wide?: boolean
}

// Voice reads these exact fields when a caller asks where the clinic is or how
// to reach it; Main phone is also where business-hours calls are forwarded.
const GROUPS: Array<{ title: string; fields: FieldConfig[] }> = [
  {
    title: 'Clinic',
    fields: [
      {
        field: 'dentistryName',
        label: 'Clinic name',
        autoComplete: 'organization',
        wide: true,
      },
      {
        field: 'dentistryAddress',
        label: 'Address',
        autoComplete: 'street-address',
        wide: true,
      },
    ],
  },
  {
    title: 'Contact',
    fields: [
      {
        field: 'dentistryPhone',
        label: 'Main phone',
        type: 'tel',
        autoComplete: 'tel',
        hint: 'Used as the front-desk forwarding number for inbound calls during business hours.',
      },
      {
        field: 'emergencyPhone',
        label: 'Emergency phone',
        type: 'tel',
        autoComplete: 'tel',
        hint: 'Given to patients who ask for an emergency number.',
      },
      {
        field: 'dentistryEmail',
        label: 'Email',
        type: 'email',
        autoComplete: 'email',
        wide: true,
      },
    ],
  },
]

export function ClinicProfileSettings({
  tenantId,
  tenantLabel,
  repository,
}: {
  tenantId: string
  tenantLabel: string
  repository: SettingsRepository
}) {
  const activeTenant = useRef(tenantId)
  activeTenant.current = tenantId
  const profile = useClinicProfile(repository, tenantId)
  const [baseline, setBaseline] = useState<ClinicInfo | null>(null)
  const [draft, setDraft] = useState<ClinicInfo>(EMPTY_CLINIC_INFO)
  const [timeZone, setTimeZone] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>(
    'idle'
  )
  const [confirmingMainPhoneChange, setConfirmingMainPhoneChange] =
    useState(false)

  useEffect(() => {
    setBaseline(null)
    setDraft(EMPTY_CLINIC_INFO)
    setTimeZone(null)
    setSaveStatus('idle')
    setConfirmingMainPhoneChange(false)
  }, [tenantId])

  useEffect(() => {
    if (!profile.data) return
    setBaseline(profile.data.clinicInfo)
    setDraft(profile.data.clinicInfo)
    setTimeZone(profile.data.timeZone)
    setSaveStatus('idle')
    setConfirmingMainPhoneChange(false)
  }, [profile.data])

  const validation = useMemo(
    () => clinicInfoPatchSchema.safeParse(draft),
    [draft]
  )
  const fieldErrors = useMemo(() => {
    const errors: Partial<Record<ClinicField, string>> = {}
    if (validation.success) return errors
    validation.error.issues.forEach((issue) => {
      const field = issue.path[0]
      if (typeof field === 'string' && field in EMPTY_CLINIC_INFO) {
        errors[field as ClinicField] ??= issue.message
      }
    })
    return errors
  }, [validation])
  const isDirty = Boolean(
    baseline && JSON.stringify(baseline) !== JSON.stringify(draft)
  )
  useReportUnsavedChanges('clinic', isDirty)

  const saveProfile = useMutation({
    mutationFn: ({
      requestTenantId,
      payload,
    }: {
      requestTenantId: string
      payload: ClinicInfo
    }) => repository.updateClinicInfo(requestTenantId, payload),
    onSuccess: (saved, variables) => {
      if (activeTenant.current !== variables.requestTenantId) return
      setBaseline(saved.clinicInfo)
      setDraft(saved.clinicInfo)
      setTimeZone(saved.timeZone)
      setSaveStatus('saved')
    },
    onError: (_error, variables) => {
      if (activeTenant.current !== variables.requestTenantId) return
      setSaveStatus('error')
    },
  })

  function updateField(field: ClinicField, value: string) {
    if (saveProfile.isPending) return
    setDraft((current) => ({ ...current, [field]: value }))
    setSaveStatus('idle')
    setConfirmingMainPhoneChange(false)
  }

  function discard() {
    if (!baseline || saveProfile.isPending) return
    setDraft(baseline)
    setSaveStatus('idle')
    setConfirmingMainPhoneChange(false)
  }

  function persist() {
    if (!isDirty || !validation.success || saveProfile.isPending) return
    setSaveStatus('idle')
    setConfirmingMainPhoneChange(false)
    saveProfile.mutate({
      requestTenantId: tenantId,
      payload: validation.data,
    })
  }

  function save() {
    if (!isDirty || !validation.success || saveProfile.isPending) return
    const currentMainPhone = normalizeClinicMainPhone(
      baseline?.dentistryPhone ?? ''
    )
    const nextMainPhone = normalizeClinicMainPhone(
      validation.data.dentistryPhone
    )
    if (
      currentMainPhone &&
      nextMainPhone &&
      currentMainPhone !== nextMainPhone
    ) {
      setConfirmingMainPhoneChange(true)
      return
    }
    persist()
  }

  const status = saveProfile.isPending ? (
    <span className={panel.statusLine}>
      <Loader2 size={15} className={panel.spin} aria-hidden="true" /> Saving…
    </span>
  ) : saveStatus === 'error' ? (
    <span className={panel.statusLine} data-tone="error">
      Clinic info could not be saved. Your changes are still here; try again.
    </span>
  ) : isDirty ? (
    <span className={panel.statusLine} data-tone="dirty">
      {validation.success
        ? 'Unsaved changes'
        : 'Unsaved changes · fix the highlighted field to save'}
    </span>
  ) : saveStatus === 'saved' ? (
    <span className={panel.statusLine} data-tone="saved">
      <Check size={16} aria-hidden="true" /> Clinic info saved
    </span>
  ) : (
    <span className={panel.statusLine}>No unsaved changes</span>
  )

  return (
    <section className={panel.section} aria-labelledby="clinic-profile-title">
      <div className={panel.panel}>
        <header className={panel.panelHeader}>
          <div className={panel.heading}>
            <span className={panel.eyebrow}>Managed in Flio</span>
            <h2 id="clinic-profile-title">Clinic profile</h2>
            <p>
              The name and contact details Flio gives patients of{' '}
              <strong>{tenantLabel}</strong> when they ask.
            </p>
          </div>
        </header>

        {profile.isPending ? (
          <div className={styles.loading}>
            <Spinner label="Loading clinic profile" />
          </div>
        ) : null}
        {profile.isError ? (
          <div className={styles.loading}>
            <StatePanel
              kind={
                isSettingsPermissionError(profile.error) ? 'forbidden' : 'error'
              }
              title="Clinic profile unavailable"
              description={
                isSettingsPermissionError(profile.error)
                  ? 'You do not have permission to manage this clinic profile.'
                  : 'This clinic profile could not be loaded safely.'
              }
            />
          </div>
        ) : null}

        {baseline ? (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              save()
            }}
          >
            <fieldset
              className={styles.groups}
              disabled={saveProfile.isPending}
            >
              <legend className={panel.srOnly}>Clinic details</legend>
              {GROUPS.map((group) => (
                <div className={styles.group} key={group.title}>
                  <h3>{group.title}</h3>
                  <div className={styles.fields}>
                    {group.fields.map(
                      ({ field, label, type, autoComplete, hint, wide }) => {
                        const errorId = `clinic-info-${field}-error`
                        const hintId = `clinic-info-${field}-hint`
                        const warningId = `clinic-info-${field}-warning`
                        const missingMainPhone =
                          field === 'dentistryPhone' &&
                          !draft.dentistryPhone.trim()
                        return (
                          <label
                            key={field}
                            className={`${panel.field} ${wide ? styles.wide : ''}`}
                          >
                            <span>{label}</span>
                            <input
                              type={type ?? 'text'}
                              autoComplete={autoComplete}
                              value={draft[field]}
                              aria-invalid={Boolean(fieldErrors[field])}
                              aria-describedby={
                                [
                                  hint ? hintId : null,
                                  missingMainPhone ? warningId : null,
                                  fieldErrors[field] ? errorId : null,
                                ]
                                  .filter(Boolean)
                                  .join(' ') || undefined
                              }
                              onChange={(event) =>
                                updateField(field, event.currentTarget.value)
                              }
                            />
                            {hint ? (
                              <small id={hintId} className={panel.hint}>
                                {hint}
                              </small>
                            ) : null}
                            {missingMainPhone ? (
                              <small
                                id={warningId}
                                className={panel.fieldWarning}
                                role="status"
                              >
                                <AlertTriangle size={14} aria-hidden="true" />
                                No Main phone configured. Calls will go directly
                                to Flio AI and will not be forwarded to the
                                clinic.
                              </small>
                            ) : null}
                            {fieldErrors[field] ? (
                              <small className={panel.fieldError} id={errorId}>
                                {fieldErrors[field]}
                              </small>
                            ) : null}
                          </label>
                        )
                      }
                    )}
                  </div>
                </div>
              ))}
            </fieldset>

            <div className={styles.readOnly}>
              <span className={styles.readOnlyIcon} aria-hidden="true">
                <Clock3 size={17} />
              </span>
              <div>
                <span className={styles.readOnlyLabel}>
                  Clinic timezone
                  <span className={styles.lockNote}>
                    <Lock size={12} aria-hidden="true" /> Read-only
                  </span>
                </span>
                <strong>{timeZone}</strong>
                <small className={panel.hint}>
                  Timezone is read-only because changing it affects active
                  booking, reporting, messaging, and call workflows.
                </small>
              </div>
            </div>

            <footer
              className={panel.panelFooter}
              data-dirty={isDirty || undefined}
            >
              <p className={panel.footerStatus} aria-live="polite">
                {status}
              </p>
              <div className={panel.footerActions}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!isDirty || saveProfile.isPending}
                  onClick={discard}
                >
                  Discard changes
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !isDirty || !validation.success || saveProfile.isPending
                  }
                >
                  {saveProfile.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </footer>
          </form>
        ) : null}
      </div>

      {confirmingMainPhoneChange ? (
        <SettingsConfirmDialog
          title="Change the front-desk forwarding number?"
          confirmLabel="Confirm and save"
          cancelLabel="Keep editing"
          onConfirm={persist}
          onCancel={() => setConfirmingMainPhoneChange(false)}
        >
          <p>
            This changes where inbound calls are forwarded during business
            hours. Future front-desk-forwarded calls will go to the new Main
            phone.
          </p>
        </SettingsConfirmDialog>
      ) : null}
    </section>
  )
}
